import { useEffect, useState } from 'react'
import {
  Anchor,
  Badge,
  Button,
  FileInput,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  useArtPmocCreate,
  useArtPmocDelete,
  useArtPmocUpdate,
  useArtsPmocList,
} from '../../hooks/useArtsPmoc'
import { TIPOS_ART, labelDe } from '../planos_modelo/constants'
import { extractApiError } from '../../lib/extractApiError'
import { useRTsEmpresaList } from '../../hooks/useRTsEmpresa'
import type { PMOC } from '../../types'
import type { ARTPMOC, ARTPMOCCreateInput, TipoART } from '../../types/pmocPlanos'

interface Props {
  pmoc: PMOC
}

const INICIAL: ARTPMOCCreateInput = {
  numero: '',
  tipo: 'planejamento',
  responsavel_tecnico: null,
  emitida_em: null,
  observacoes: '',
  ordem: 0,
  arquivo: null,
}

export default function PmocArtsTab({ pmoc }: Props) {
  const editavel = pmoc.status === 'rascunho'
  const lista = useArtsPmocList(pmoc.id)
  const create = useArtPmocCreate(pmoc.id)
  const update = useArtPmocUpdate(pmoc.id)
  const remove = useArtPmocDelete(pmoc.id)
  const rts = useRTsEmpresaList({ page_size: 500 })

  const [aberto, setAberto] = useState(false)
  const [editandoId, setEditandoId] = useState<string | null>(null)
  const [form, setForm] = useState<ARTPMOCCreateInput>(INICIAL)

  useEffect(() => {
    if (!aberto) {
      setForm(INICIAL)
      setEditandoId(null)
    }
  }, [aberto])

  function abrirNovo() {
    setForm({ ...INICIAL, ordem: (lista.data?.results.length ?? 0) + 1 })
    setEditandoId(null)
    setAberto(true)
  }

  function abrirEdicao(a: ARTPMOC) {
    setForm({
      numero: a.numero,
      tipo: a.tipo,
      responsavel_tecnico: a.responsavel_tecnico,
      emitida_em: a.emitida_em,
      observacoes: a.observacoes,
      ordem: a.ordem,
      arquivo: null,
    })
    setEditandoId(a.id)
    setAberto(true)
  }

  async function handleSalvar() {
    if (!form.numero.trim()) {
      notifications.show({ color: 'red', message: 'Número da ART é obrigatório.' })
      return
    }
    try {
      if (editandoId) {
        await update.mutateAsync({ id: editandoId, data: form })
        notifications.show({ color: 'green', message: 'ART atualizada.' })
      } else {
        await create.mutateAsync(form)
        notifications.show({ color: 'green', message: 'ART criada.' })
      }
      setAberto(false)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao salvar ART.'),
      })
    }
  }

  async function handleRemover(a: ARTPMOC) {
    if (!confirm(`Remover ART "${a.numero}" deste PMOC?`)) return
    try {
      await remove.mutateAsync(a.id)
      notifications.show({ color: 'green', message: 'ART removida.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao remover.'),
      })
    }
  }

  const semRT = form.responsavel_tecnico === null || form.responsavel_tecnico === ''

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={4}>ARTs do PMOC</Title>
          <Text size="xs" c="dimmed">
            Anotações de Responsabilidade Técnica vinculadas a este PMOC.
            Múltiplas ARTs por PMOC (planejamento, execução, QAI, etc.).
          </Text>
        </div>
        {editavel && (
          <Button onClick={abrirNovo} data-testid="art-novo-btn">
            + Nova ART
          </Button>
        )}
      </Group>

      {!editavel && (
        <Alert color="orange" variant="light">
          PMOC em status <strong>{pmoc.status}</strong> — ARTs não editáveis.
        </Alert>
      )}

      {lista.isLoading ? (
        <Text c="dimmed">Carregando ARTs…</Text>
      ) : (lista.data?.results.length ?? 0) === 0 ? (
        <Paper p="lg" withBorder>
          <Text c="dimmed">Nenhuma ART cadastrada neste PMOC.</Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Número</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>RT vinculado</Table.Th>
                <Table.Th>Emitida em</Table.Th>
                <Table.Th>Arquivo</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lista.data?.results.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td>
                    <Text fw={600}>{a.numero}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{labelDe(a.tipo, TIPOS_ART)}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {a.responsavel_tecnico ? (
                      <Text size="xs">RT {a.responsavel_tecnico.slice(0, 8)}</Text>
                    ) : (
                      <Badge color="orange" variant="light">
                        sem RT
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{a.emitida_em ?? '—'}</Table.Td>
                  <Table.Td>
                    {a.arquivo_url ? (
                      <Anchor href={a.arquivo_url} target="_blank" size="sm">
                        Abrir PDF
                      </Anchor>
                    ) : (
                      <Text size="xs" c="dimmed">
                        sem arquivo
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {editavel && (
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => abrirEdicao(a)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="subtle"
                          onClick={() => handleRemover(a)}
                        >
                          Remover
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={aberto}
        onClose={() => setAberto(false)}
        title={editandoId ? 'Editar ART' : 'Nova ART do PMOC'}
        size="lg"
        centered
      >
        <Stack gap="sm">
          <Group grow>
            <TextInput
              label="Número da ART/RRT"
              required
              value={form.numero}
              onChange={(e) => setForm({ ...form, numero: e.currentTarget.value })}
              data-testid="art-numero"
            />
            <Select
              label="Tipo"
              data={TIPOS_ART}
              value={form.tipo}
              onChange={(v) => v && setForm({ ...form, tipo: v as TipoART })}
            />
          </Group>
          <Select
            label="RT vinculado (opcional)"
            placeholder="Selecione um RT da empresa…"
            data={(rts.data?.results ?? []).map((r) => ({
              value: r.id,
              label: `${r.nome} — ${r.conselho} ${r.numero_registro}/${r.uf}`,
            }))}
            value={form.responsavel_tecnico ?? null}
            onChange={(v) => setForm({ ...form, responsavel_tecnico: v })}
            clearable
            searchable
          />
          {semRT && (
            <Alert color="orange" variant="light">
              ART sem RT vinculado. Permitido pelo backend, mas recomenda-se
              registrar o responsável técnico.
            </Alert>
          )}
          <TextInput
            label="Emitida em"
            type="date"
            value={form.emitida_em ?? ''}
            onChange={(e) =>
              setForm({ ...form, emitida_em: e.currentTarget.value || null })
            }
          />
          <FileInput
            label="Arquivo (PDF) — opcional"
            placeholder="Selecione o PDF da ART"
            accept="application/pdf"
            value={form.arquivo ?? null}
            onChange={(f) => setForm({ ...form, arquivo: f })}
            clearable
            data-testid="art-arquivo"
          />
          <Textarea
            label="Observações"
            autosize
            minRows={1}
            value={form.observacoes ?? ''}
            onChange={(e) =>
              setForm({ ...form, observacoes: e.currentTarget.value })
            }
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSalvar}
              loading={create.isPending || update.isPending}
              data-testid="art-submit"
            >
              {editandoId ? 'Salvar' : 'Criar ART'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
